<template>
  <ChartCard :data-widget-id="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="gender-by-stage"
    />
    <ChartContent
      height="292px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="lineChart"
      class="px-3 pt-2 pb-0"
    >
      <LineChart :id="widgetId" :data="chartData" :options="chartOptions" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import LineChart from '@/components/insights/Chart/LineChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import { customTooltip } from '@/components/insights/Chart/oldChartTooltip';
import { useTracking } from '@/stores/tracking/tracking';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import { calculatePercent } from '@/components/insights/helpers';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const noWidgetData = computed(() => !props.data
  || Object.keys(props.data).length === 0
  || Object.keys(props.data).every((key) => {
    const stageData = props.data[key];
    return !stageData || Object.values(stageData).every((value) => !value);
  }));

const widgetLabel = 'What is the gender representation by stage?';

const widgetId = 'gender-representation-by-stage';

const { candidateFemaleInferred, candidateMaleInferred, candidateOther } = useColors();
const { lineChartHover } = useTracking();

const chartData = computed(() => {
  const { female, male, unknown } = props.data || {};
  const {
    review: femaleReview,
    screen: femaleScreen,
    assessment: femaleAssessment,
    offer: femaleOffer,
    hired: femaleHired,
  } = female || {};
  const {
    review: maleReview,
    screen: maleScreen,
    assessment: maleAssessment,
    offer: maleOffer,
    hired: maleHired,
  } = male || {};
  const {
    review: unknownReview,
    screen: unknownScreen,
    assessment: unknownAssessment,
    offer: unknownOffer,
    hired: unknownHired,
  } = unknown || {};

  const reviewTotal = femaleReview + maleReview + unknownReview;
  const screenTotal = femaleScreen + maleScreen + unknownScreen;
  const assessmentTotal = femaleAssessment + maleAssessment + unknownAssessment;
  const offerTotal = femaleOffer + maleOffer + unknownOffer;
  const hiredTotal = femaleHired + maleHired + unknownHired;
  return {
    labels: ['Applied', 'Screening', 'Assessment', 'Offer', 'Hired'],
    datasets: [
      {
        label: 'Female',
        backgroundColor: candidateFemaleInferred.value,
        borderColor: candidateFemaleInferred.value,
        fill: false,
        pointRadius: 4,
        tension: 0,
        data: [
          calculatePercent(femaleReview, reviewTotal),
          calculatePercent(femaleScreen, screenTotal),
          calculatePercent(femaleAssessment, assessmentTotal),
          calculatePercent(femaleOffer, offerTotal),
          calculatePercent(femaleHired, hiredTotal),
        ],
      },
      {
        label: 'Male',
        backgroundColor: candidateMaleInferred.value,
        borderColor: candidateMaleInferred.value,
        fill: false,
        pointRadius: 4,
        tension: 0,
        data: [
          calculatePercent(maleReview, reviewTotal),
          calculatePercent(maleScreen, screenTotal),
          calculatePercent(maleAssessment, assessmentTotal),
          calculatePercent(maleOffer, offerTotal),
          calculatePercent(maleHired, hiredTotal),
        ],
      },
      {
        label: 'Unknown',
        backgroundColor: candidateOther.value,
        borderColor: candidateOther.value,
        fill: false,
        pointRadius: 4,
        tension: 0,
        data: [
          calculatePercent(unknownReview, reviewTotal),
          calculatePercent(unknownScreen, screenTotal),
          calculatePercent(unknownAssessment, assessmentTotal),
          calculatePercent(unknownOffer, offerTotal),
          calculatePercent(unknownHired, hiredTotal),
        ],
      },
    ],
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    legend: {
      display: true,
      position: 'right',
      align: 'start',
      maxWidth: 200,
      onClick: () => {},
      labels: {
        boxWidth: 12,
        boxHeight: 12,
        generateLabels: (chart) => chart.data.datasets.map((dataset) => ({
          text: dataset.label,
          fillStyle: dataset.borderColor,
          strokeStyle: dataset.borderColor,
          borderRadius: 4,
        })),
      },
    },
    tooltip: {
      mode: 'index',
      enabled: false,
      external: customTooltip('basicChart', {
        chartData: {
          originalLabels: chartData.value.labels,
          datasets: chartData.value.datasets,
        },
        labelFormat: 'percent',
        onHover() {
          lineChartHover();
        },
      }, widgetId),
      bodyFont: {
        size: 16,
        family: 'Inter',
      },
    },
  },

  scales: {
    y: {
      offset: true,
      title: {
        display: false,
      },
      beginAtZero: true,
      border: {
        display: false,
      },
      grid: {
        display: false,
      },
      ticks: {
        stepSize: 10,
        callback: function yTickCallback(value) {
          return `${value}%`;
        },
      },
    },
    x: {
      border: {
        display: false,
        dash: [2, 3],
      },
      grid: {
        display: true,
        color: candidateOther.value,
        tickLength: 0,
      },
      ticks: {
        padding: 24,
      },
    },
  },
}));
</script>
